import type { SupabaseClient } from '@supabase/supabase-js';
import type { AMIDataPoint } from '../types/project';

// Single gateway for every full-series READ of ami_data_points. The write side
// lives in amiPersistence.ts; this is its counterpart.
//
// A project's series is far larger than one PostgREST response can carry —
// production averages ~35,000 interval rows per project — so the read is paged.
// Two things about how it pages matter:
//
//   1. KEYSET, not OFFSET. The previous implementation used .range(), which is
//      LIMIT/OFFSET: page 35 made Postgres walk and discard the 34,000 rows
//      ahead of it, so the cost of a page grew with its position and the whole
//      loop was quadratic. Seeking on the last timestamp instead makes every
//      page cost the same as the first.
//
//   2. ORDERED, which the previous implementation was not. Postgres guarantees
//      no row order without ORDER BY, and that guarantee does not exist ACROSS
//      separate LIMIT/OFFSET statements either — so paging an unordered query
//      may silently repeat or skip rows. Ordering by timestamp fixes that, and
//      UNIQUE (project_id, timestamp) is what makes the seek exact: within one
//      project no two rows share a timestamp, so `> last` can neither skip a
//      row nor return one twice.
//
// The ordering also matches the index that already exists for the unique
// constraint, so it costs nothing.

const PAGE_SIZE = 1000;

/**
 * Reads every AMI interval row for a project, oldest first.
 *
 * Throws on any page failure rather than returning what it managed to collect.
 * A truncated series is worse than no series here: it silently lowers the peak
 * demand, and peak demand is the entire NEC 220.87 result.
 */
export async function fetchAllAmiPoints(
  client: SupabaseClient,
  projectId: string
): Promise<AMIDataPoint[]> {
  const all: AMIDataPoint[] = [];
  let after: string | null = null;

  while (true) {
    let query = client
      .from('ami_data_points')
      .select('*')
      .eq('project_id', projectId)
      .order('timestamp', { ascending: true })
      .limit(PAGE_SIZE);

    if (after !== null) query = query.gt('timestamp', after);

    const { data, error } = await query;
    if (error) throw error;
    if (!data || data.length === 0) break;

    all.push(...(data as AMIDataPoint[]));
    if (data.length < PAGE_SIZE) break;

    after = (data[data.length - 1] as AMIDataPoint).timestamp;
  }

  return all;
}
